import React from 'react'
import dayjs from 'dayjs'
import { Box } from '@ally/metronome-ui'
import relativeTime from 'dayjs/plugin/relativeTime'
import { CustomerDomain, AfgCustomerDomain } from '@ally/data-types-host'

import { getLoyaltyType, LoyaltyType } from '../utils/loyalty'
import { StyledBadgeIcon, StyledBadgeHeading } from './StyledComponents'

import LoyaltyBadgeIconTier1 from '../../../../static-assets/images/badges/loyalty/loyalty-tier-1.svg'
import LoyaltyBadgeIconTier2 from '../../../../static-assets/images/badges/loyalty/loyalty-tier-2.svg'
import LoyaltyBadgeIconTier3 from '../../../../static-assets/images/badges/loyalty/loyalty-tier-3.svg'

dayjs.extend(relativeTime)

interface TextProps {
  type: string
  year: string | number
}
interface Props {
  legacyCustomer: CustomerDomain | null
  afgCustomer: AfgCustomerDomain | null
}

const Icon: React.FC<{ type: LoyaltyType }> = ({ type }) => {
  const IconLookup = {
    FALLBACK: (
      <LoyaltyBadgeIconTier1
        data-testid="tier1-loyalty-badge"
        title="Illustration: Pink flag badge with stars and sunbeams featuring a silver and teal key with a purple gemstone in the handle unlocking your future with Ally."
      />
    ),
    TIER1: (
      <LoyaltyBadgeIconTier1
        data-testid="tier1-loyalty-badge"
        title="Illustration: Pink flag badge with stars and sunbeams featuring a silver and teal key with a purple gemstone in the handle unlocking your future with Ally."
      />
    ),
    TIER2: (
      <LoyaltyBadgeIconTier2
        data-testid="tier2-loyalty-badge"
        title="Illustration: Purple striped flag badge featuring two crisscrossed light purple flags with pink stars and mint green flagpole ball toppers celebrating your 5-10 years with Ally."
      />
    ),
    TIER3: (
      <LoyaltyBadgeIconTier3
        data-testid="tier3-loyalty-badge"
        title="Illustration: Deep purple flag badge featuring a light purple crown with multicolored gemstones and three points topped with more gemstones to celebrate your 10+ years with Ally."
      />
    ),
  }
  return <StyledBadgeIcon>{IconLookup[type]}</StyledBadgeIcon>
}

const Heading: React.FC<{ type: LoyaltyType }> = ({ type }) => {
  const HeadingLookup = {
    FALLBACK: 'Ally Go-Getter',
    TIER1: 'Ally Go-Getter',
    TIER2: 'Ally Achiever',
    TIER3: 'Ally Mogul',
  }
  return <StyledBadgeHeading>{HeadingLookup[type]}</StyledBadgeHeading>
}

const Text: React.FC<TextProps> = ({ type, year }) => {
  if (type === 'FALLBACK') {
    return <p>You&apos;ve unlocked a bright financial future with Ally.</p>
  }

  return <p>Working together since {year}.</p>
}

const LoyaltyBadge: React.FC<Props> = ({ legacyCustomer, afgCustomer }) => {
  const legacyAccountOpeningDate = legacyCustomer?.customer?.accountOpeningDate
  const afgAccountOpeningDate = afgCustomer?.data?.sinceDate
  const accountOpeningDate = legacyAccountOpeningDate ?? afgAccountOpeningDate

  const loyaltyType = getLoyaltyType(accountOpeningDate ?? '')
  const memberSinceYear = dayjs(accountOpeningDate).year() || ''

  return (
    <Box marginTop="6px" marginBottom="6px" display="flex" flexDirection="row">
      <Icon type={loyaltyType} />
      <Box
        marginRight="sm"
        marginTop="4px"
        borderBottom="1px solid"
        borderColor="slate-2"
        width="100%"
      >
        <Heading type={loyaltyType} />
        <Text type={loyaltyType} year={memberSinceYear} />
      </Box>
    </Box>
  )
}

export { LoyaltyBadge }
